<template>
  <v-card class="base-card shadow">
    <v-card-title class="headline accent--text pb-4">
      {{ title }}
    </v-card-title>

    <v-card-text>
      <CardRow>
        <v-checkbox
          v-if="isEffContractVisibility"
          class="rounded-lg mini-text field-edit d-inline-block"
          v-model="publication.commonWork.isEffContract"
          label="Работа входит в эффективный контракт"
          :disabled="isShowMode && !canChangeIsEffContractInShowMode"
          hide-details
          dense
          @change="updatePublication"
        />

        <br />

        <v-checkbox
          class="mt-4 rounded-lg mini-text field-edit d-inline-block"
          v-model="publication.isForeignPeriodMagazine"
          label="Статья в зарубежном издании"
          hide-details
          dense
          :disabled="isShowMode"
          @change="updatePublication"
        />
      </CardRow>

      <CardAutocompleteRow
        v-model="publication.commonWork.year"
        :items="yearList"
        item-value="id"
        item-text="year"
        label="Год публикации"
        :no-filter="false"
        placeholder="Выберите год публикации"
        :disabled="isShowMode"
        :error-messages="errorMessages.year"
        :required="!isShowMode"
        @input="
          $v.publication.commonWork.year.$touch()
          updatePublication()
        "
      />

      <CardFieldRow
        v-model="publication.commonWork.workName"
        label="Наименование статьи на языке оригинала публикации"
        placeholder="Добавьте наименование"
        :disabled="isShowMode"
        :error-messages="errorMessages.workName"
        :required="!isShowMode"
        @input="
          $v.publication.commonWork.workName.$touch()
          updatePublication()
        "
      />

      <CardFieldRow
        v-if="publication.isForeign"
        v-model="publication.publicationEngName"
        label="Наименование статьи на английском языке/транслитерация"
        placeholder="Добавьте наименование"
        :disabled="isShowMode"
        :error-messages="errorMessages.publicationEngName"
        :required="!isShowMode && publication.isForeign"
        @input="
          $v.publication.publicationEngName.$touch()
          updatePublication()
        "
      />

      <CardAutocompleteRow
        v-if="!publication.conferenceMaterial"
        v-model="publication.magazine"
        :items="magazineList.list"
        item-value="id"
        item-text="name"
        label="Наименование издания"
        placeholder="Введите не менее 3-х символов"
        hide-no-data
        :loading="magazineListLoading"
        :disabled="isShowMode"
        :error-messages="errorMessages.magazine"
        :required="!isShowMode"
        @input="
          $v.publication.magazine.$touch()
          updatePublication()
        "
        @searchData="getMagazineList"
      />

      <CardFieldRow
        v-else
        label="Наименование издания"
        placeholder="Добавьте наименование издания"
        v-model="publication.magazineName"
        :disabled="isShowMode"
        :error-messages="errorMessages.magazineName"
        :required="!isShowMode"
        @input="
          $v.publication.magazineName.$touch()
          updatePublication()
        "
      />

      <CardRow>
        <v-checkbox
          class="rounded-lg mini-text field-edit d-inline-block"
          v-model="publication.conferenceMaterial"
          label="Сборник"
          hide-details
          dense
          :disabled="isShowMode"
          @change="resetMagazine(), updatePublication()"
        />

        <br />

        <v-checkbox
          class="mt-4 rounded-lg mini-text field-edit d-inline-block"
          v-model="publication.isVac"
          label="Входит в перечень ВАК"
          hide-details
          dense
          :disabled="isShowMode"
          @change="resetCatalog(), updatePublication()"
        />

        <br />

        <v-checkbox
          class="mt-4 rounded-lg mini-text field-edit d-inline-block"
          v-model="publication.isWhiteList"
          label="Входит в 'Белый список'"
          hide-details
          dense
          :disabled="isShowMode"
          @change="resetWhiteListLevel(), updatePublication()"
        />
      </CardRow>

      <CardAutocompleteRow
        v-if="publication.isVac"
        v-model="publication.catalog"
        :items="catalogList"
        item-text="name"
        item-value="id"
        label="Категорирование"
        :return-object="true"
        :disabled="isShowMode"
        :error-messages="errorMessages.catalog"
        :required="!isShowMode"
        @input="
          $v.publication.catalog.$touch()
          updatePublication()
        "
      />

      <CardAutocompleteRow
        v-if="publication.isWhiteList"
        v-model="publication.whiteListLevel"
        :items="whiteListLevelList"
        item-text="name"
        item-value="id"
        label="Уровень 'Белого списка'"
        :return-object="true"
        :disabled="isShowMode"
        :error-messages="errorMessages.whiteListLevel"
        :required="!isShowMode"
        @input="
          $v.publication.whiteListLevel.$touch()
          updatePublication()
        "
      />

      <CardFieldRow
        v-model="publication.doi"
        label="DOI электронных изданий"
        placeholder="Добавьте DOI электронных изданий"
        :disabled="isShowMode"
        :error-messages="errorMessages.doi"
        @input="
          $v.publication.doi.$touch()
          updatePublication()
        "
      />

      <CardFieldRow
        v-model="publication.tome"
        type="number"
        label="Номер тома"
        placeholder="Добавьте номер тома"
        :disabled="isShowMode"
        :error-messages="errorMessages.tome"
        @input="
          $v.publication.tome.$touch()
          updatePublication()
        "
      />

      <CardFieldRow
        v-if="isShowMode"
        v-model="publication.partNumber"
        label="Номер части тома"
        placeholder="Добавьте номер части тома"
        :disabled="isShowMode"
        :use-vmask-for-number="true"
        @input="updatePublication()"
      />

      <CardFieldRow
        v-else
        v-model="publication.partNumber"
        v-mask="'#########'"
        label="Номер части тома"
        placeholder="Добавьте номер части тома"
        :disabled="isShowMode"
        :use-vmask-for-number="true"
        @input="updatePublication()"
      />

      <CardFieldRow
        v-model="publication.magazineNumber"
        label="Номер журнала"
        placeholder="Добавьте номер журнала"
        :disabled="isShowMode"
        :error-messages="errorMessages.magazineNumber"
        :required="!isShowMode && !publication.conferenceMaterial"
        @input="
          $v.publication.magazineNumber.$touch()
          updatePublication()
        "
      />

      <CardRadioRow
        v-model="publication.publicationForm"
        label="Форма издания"
        hide-border-bottom
        :disabled="isShowMode"
        :value-list="publicationFormList"
        :required="!isShowMode"
        @change="
          $v.publication.publicationForm.$touch()
          updatePublication()
        "
      />
    </v-card-text>
  </v-card>
</template>

<script>
import { MagazineParent, Publication } from '@/models'
import { setValidateMessages } from '@/scripts/validation'
import CardRow from '@/components/ui/row/CardRow'
import CardFieldRow from '@/components/ui/row/CardFieldRow'
import CardAutocompleteRow from '@/components/ui/row/CardAutocompleteRow'
import CardRadioRow from '@/components/ui/row/CardRadioRow.vue'
import { required, maxLength, requiredIf } from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex'
import _debounce from 'lodash/debounce'
import { magazineApi } from '@/api'
import { isEffContractMixin } from '@/mixins/isEffContract'

export default {
  components: {
    CardAutocompleteRow,
    CardRow,
    CardFieldRow,
    CardRadioRow
  },
  props: {
    isShowMode: Boolean,
    saveBtnLoading: Boolean,
    dialog: Boolean,
    selectedItem: Object,
    title: String
  },

  mixins: [isEffContractMixin],

  mounted() {
    this.updateInvalid()
  },

  data: () => ({
    publication: new Publication(),
    magazineList: new MagazineParent(),
    magazineListLoading: false
  }),

  validations() {
    return {
      publication: {
        commonWork: {
          workName: { required, maxLength: maxLength(1500) },
          year: { required }
        },

        publicationEngName: { required: requiredIf(item => item.isForeign), maxLength: maxLength(1500) },
        magazine: { required: requiredIf(item => !item.conferenceMaterial) },
        magazineName: { required: requiredIf(item => item.conferenceMaterial) },
        doi: { maxLength: maxLength(100) },
        tome: { maxLength: maxLength(30) },
        magazineNumber: { required: requiredIf(item => !item.conferenceMaterial), maxLength: maxLength(20) },
        publicationForm: { required },
        catalog: { required: requiredIf(item => item.isVac) },
        whiteListLevel: { required: requiredIf(item => item.isWhiteList) }
      }
    }
  },

  computed: {
    ...mapGetters('dictionaries', ['yearList', 'publicationFormList', 'catalogList', 'whiteListLevelList']),
    ...mapGetters('user', ['isAdmin', 'isModerator', 'isModeratorNir']),

    currentWork() {
      return this.publication
    },

    errorMessages() {
      const allErrors = {}

      Object.keys(this.$v.publication.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.publication[key])
      })

      Object.keys(this.$v.publication.commonWork.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.publication.commonWork[key])
      })

      return allErrors
    }
  },

  methods: {
    resetMagazine() {
      this.publication.magazine = null
      this.publication.magazineName = null
    },

    resetCatalog() {
      this.publication.catalog = null
    },

    resetWhiteListLevel() {
      this.publication.whiteListLevel = null
    },

    resetPublicationEngName() {
      this.publication.publicationEngName = null
    },

    updatePublication() {
      this.$emit('update:selected-item', new Publication(this.publication.getForAPI()))
      this.updateInvalid()
    },

    updateInvalid() {
      this.$emit('updateInvalid', this.$v.$invalid)
    },

    getMagazineList: _debounce(async function (val) {
      try {
        if (!val) {
          this.publication.magazine = null
          this.magazineList = new MagazineParent()
          this.magazineListLoading = false
          return
        }

        if (val?.length >= 3 && this.publication?.magazine?.name !== val) {
          this.magazineListLoading = true
          const data = await magazineApi.findAllByName(val)
          this.magazineList = new MagazineParent(data)
          if (this.publication.magazine) this.magazineList.add(this.publication.magazine)
        }
        this.magazineListLoading = false
      } catch (e) {
        console.log(e)
        if (e) {
          this.magazineListLoading = false
          this.showErrorMessage(e, 'Не удалось получить список журналов')
        } else this.magazineListLoading = true
      }
    }, 500)
  },

  watch: {
    selectedItem: {
      immediate: true,
      deep: true,
      handler(val) {
        if (this.selectedItem?.magazine) this.magazineList.add(this.selectedItem.magazine)
        this.publication = new Publication(val.getForAPI())
        this.updateInvalid()
      }
    }
  }
}
</script>

<style scoped></style>
